import type { OnInit } from '@angular/core';
import { Component, inject, input, ViewEncapsulation } from '@angular/core';

import type { ContentMargin } from '@valk-nx/components/ui-hero-item/src/lib/content-margin';
import { HeroItem } from '@valk-nx/components/ui-hero-item/src/lib/hero-item';
import type {
	Alignment,
	VerticalAlignment,
} from '@valk-nx/components/ui-hero-item/src/lib/hero-item.interface';
import { HeroItemSubHeading } from '@valk-nx/components/ui-hero-item/src/lib/hero-item-sub-heading';
import { SliderNavButtonComponent } from '@valk-nx/components/ui-slider/src/lib/nav-button/nav-button';
import { SliderComponent } from '@valk-nx/components/ui-slider/src/lib/slider';
import type { SliderConfig } from '@valk-nx/components/ui-slider/src/lib/slider.interface';
import { cn } from '@valk-nx/core/lib/core';
import type { VimeoInitialQuality } from '@valk-nx/core/lib/interfaces/video.interface';
import { PreloadService } from '@valk-nx/core/lib/pipes/image-query/image-preload.service';
import { ImageQueryService } from '@valk-nx/core/lib/pipes/image-query/image-query.service';

import type { CarouselItem } from './carousel.interface';

/** One hero per view, wrapping around; the wrapper's aspect classes set the height. */
const CAROUSEL_SLIDER_CONFIG: SliderConfig = {
	slidesPerView: 1,
	gap: 0,
	edgeOffset: 0,
	loop: true,
	showNavigation: true,
	showPagination: false,
	fillHeight: true,
	breakpoints: {},
};

@Component({
	encapsulation: ViewEncapsulation.None,
	selector: 'vp-carousel',
	templateUrl: './carousel.html',
	imports: [
		HeroItem,
		HeroItemSubHeading,
		SliderComponent,
		SliderNavButtonComponent,
	],
})
export class CarouselComponent implements OnInit {
	private readonly imageQuery = inject(ImageQueryService);
	private readonly preloadService = inject(PreloadService);

	items = input.required<CarouselItem[]>();
	size = input<'small' | 'large'>('large');
	useEagerLoading = input<boolean>(true);

	/** Rendition Vimeo slides start on; leave unset for Vimeo's own `auto`. */
	videoInitialQuality = input<VimeoInitialQuality>();

	contentMargin = input<ContentMargin>('none');
	alignment = input<Alignment>('left');
	verticalAlignment = input<VerticalAlignment>('bottom');

	cn = cn;

	protected readonly sliderConfig: SliderConfig = CAROUSEL_SLIDER_CONFIG;

	ngOnInit() {
		const preloadUrl = this.getPreloadUrl(
			this.items()[0]?.image?.src,
			this.size(),
		);
		this.preloadService.preloadImage(preloadUrl);
	}

	getPreloadUrl(src: string | undefined, size: 'small' | 'large'): string {
		if (!src) return '';

		const targetWidth = 800;
		const targetHeight = size === 'large' ? 640 : 320;
		return this.imageQuery.transform(src, {
			width: targetWidth,
			height: targetHeight,
		});
	}
}
